/**
 * LegalTemplates PDF Editor — find in document
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { redrawAllPages } from './annotations.js';
import {
    findBarEl,
    findCountEl,
    findInputEl,
    findNextButton,
    findPrevButton,
    pagesEl,
    state,
} from './context.js';
import { hasLoadedDocumentForActions } from './core.js';
import { closeShellMenus, repositionAnchoredChrome } from './shell.js';
import { showAlert } from './ui.js';
import {
    ensureAllPagesPrepared,
    ensurePagePrepared,
    getScrollContainer,
    getZoom,
    scheduleViewportWork,
    setActiveThumb,
    syncPageIndicator,
} from './viewport.js';

// ------------------------------------------------------------------
// Find in document
//
// Searches the extracted text fragments of every page. Fragments on the
// same baseline are joined into a line so a phrase can span runs; each
// match keeps the boxes of the fragments it touches for highlighting.
// ------------------------------------------------------------------
export function buildPageSearchLines(pageNumber) {
    const fragments = (state.pageTextFragments.get(pageNumber) || [])
        .slice()
        .sort((a, b) => (a.baselineY - b.baselineY) || (a.x - b.x));
    const lines = [];
    let current = null;
    fragments.forEach((fragment) => {
        const tolerance = Math.max(3, Number(fragment.height || 0) * 0.45);
        if (current && Math.abs(Number(fragment.baselineY) - current.baselineY) <= tolerance) {
            current.fragments.push(fragment);
        } else {
            current = { baselineY: Number(fragment.baselineY), fragments: [fragment] };
            lines.push(current);
        }
    });
    return lines.map((line) => {
        line.fragments.sort((a, b) => a.x - b.x);
        let text = '';
        const spans = [];
        line.fragments.forEach((fragment, index) => {
            if (index > 0) {
                text += ' ';
            }
            const start = text.length;
            text += String(fragment.text || '');
            spans.push({ fragment, start, end: text.length });
        });
        return { pageNumber, text, spans };
    });
}

export function buildFindMatch(line, start, end) {
    const boxes = [];
    line.spans.forEach(({ fragment, start: spanStart, end: spanEnd }) => {
        const overlapStart = Math.max(start, spanStart);
        const overlapEnd = Math.min(end, spanEnd);
        if (overlapEnd <= overlapStart) {
            return;
        }
        const length = Math.max(1, spanEnd - spanStart);
        const x0 = fragment.x + (fragment.width * ((overlapStart - spanStart) / length));
        const x1 = fragment.x + (fragment.width * ((overlapEnd - spanStart) / length));
        boxes.push({
            x: x0,
            y: fragment.y,
            width: Math.max(2, x1 - x0),
            height: fragment.height,
        });
    });
    const first = boxes[0] || { x: 0, y: 0 };
    return { page: line.pageNumber, boxes, x: first.x, y: first.y };
}

export function collectFindMatches(query) {
    const needle = String(query || '').trim().toLowerCase();
    const matches = [];
    if (!needle || !state.pdfJsDoc) {
        return matches;
    }
    const pageCount = state.pdfJsDoc.numPages;
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
        buildPageSearchLines(pageNumber).forEach((line) => {
            const haystack = line.text.toLowerCase();
            let from = 0;
            while (from <= haystack.length) {
                const index = haystack.indexOf(needle, from);
                if (index < 0) {
                    break;
                }
                matches.push(buildFindMatch(line, index, index + needle.length));
                from = index + Math.max(1, needle.length);
            }
        });
    }
    return matches;
}

export function getFindOverlaySvg(pageNumber) {
    if (!state.find.open || !state.find.matches.length) {
        return '';
    }
    let svg = '<g class="pdf-find-layer" pointer-events="none">';
    state.find.matches.forEach((match, index) => {
        if (match.page !== pageNumber) {
            return;
        }
        const isCurrent = index === state.find.index;
        match.boxes.forEach((box) => {
            svg += `<rect x="${(box.x - 1).toFixed(1)}" y="${(box.y - 1).toFixed(1)}" width="${(box.width + 2).toFixed(1)}" height="${(box.height + 2).toFixed(1)}" rx="2" fill="${isCurrent ? 'rgba(249, 115, 22, 0.42)' : 'rgba(250, 204, 21, 0.38)'}" stroke="${isCurrent ? '#ea580c' : 'none'}" stroke-width="1"></rect>`;
        });
    });
    return `${svg}</g>`;
}

export function syncFindUi() {
    if (!findCountEl) {
        return;
    }
    const total = state.find.matches.length;
    if (!state.find.query.trim()) {
        findCountEl.textContent = '';
    } else if (!total) {
        findCountEl.textContent = 'No matches';
    } else {
        findCountEl.textContent = `${state.find.index + 1} of ${total}`;
    }
    if (findPrevButton) {
        findPrevButton.disabled = total === 0;
    }
    if (findNextButton) {
        findNextButton.disabled = total === 0;
    }
}

export function openFindBar() {
    if (!findBarEl || !findInputEl) {
        return;
    }
    if (!hasLoadedDocumentForActions()) {
        showAlert('Upload a PDF first.', true);
        return;
    }
    closeShellMenus();
    findBarEl.hidden = false;
    repositionAnchoredChrome();
    state.find.open = true;
    findInputEl.focus();
    findInputEl.select();
    if (findInputEl.value.trim()) {
        runFind(findInputEl.value).catch(() => {});
    }
}

export function closeFindBar() {
    if (!findBarEl || findBarEl.hidden) {
        return;
    }
    window.clearTimeout(state.find.timer);
    findBarEl.hidden = true;
    state.find.open = false;
    state.find.matches = [];
    state.find.index = -1;
    redrawAllPages();
    syncFindUi();
}

export function scheduleFind(value) {
    window.clearTimeout(state.find.timer);
    state.find.timer = window.setTimeout(() => {
        runFind(value).catch(() => {});
    }, 180);
}

export async function runFind(value) {
    const query = String(value || '');
    state.find.query = query;
    const token = ++state.find.token;
    if (!query.trim()) {
        state.find.matches = [];
        state.find.index = -1;
        redrawAllPages();
        syncFindUi();
        return;
    }
    if (state.pdfJsDoc && state.pagesReady.size < state.pdfJsDoc.numPages) {
        if (findCountEl) {
            findCountEl.textContent = 'Preparing pages...';
        }
        await ensureAllPagesPrepared();
        if (token !== state.find.token) {
            return;
        }
    }
    state.find.matches = collectFindMatches(query);
    state.find.index = state.find.matches.length ? 0 : -1;
    redrawAllPages();
    syncFindUi();
    if (state.find.index >= 0) {
        scrollMatchIntoView(state.find.matches[state.find.index]);
    }
}

export function stepFind(direction) {
    const total = state.find.matches.length;
    if (!total) {
        return;
    }
    state.find.index = (state.find.index + direction + total) % total;
    redrawAllPages();
    syncFindUi();
    scrollMatchIntoView(state.find.matches[state.find.index]);
}

export function scrollMatchIntoView(match) {
    const pageShell = pagesEl.querySelector(`[data-page="${match.page}"]`);
    if (!pageShell) {
        return;
    }
    ensurePagePrepared(match.page).catch(() => {});
    const zoom = getZoom();
    const rect = pageShell.getBoundingClientRect();
    const targetY = rect.top + (match.y * zoom);
    const scroller = getScrollContainer();
    const scrollerIsWindow = scroller === document.scrollingElement || scroller === document.documentElement;
    const viewportHeight = scrollerIsWindow ? window.innerHeight : scroller.clientHeight;
    const scrollerTop = scrollerIsWindow ? 0 : scroller.getBoundingClientRect().top;
    const delta = (targetY - scrollerTop) - (viewportHeight / 2);
    if (scrollerIsWindow) {
        window.scrollBy(0, delta);
    } else {
        scroller.scrollTop += delta;
    }
    state.lastActivePage = match.page;
    setActiveThumb(match.page);
    syncPageIndicator(match.page);
    scheduleViewportWork();
}
